import { useEffect, useState, useRef, useCallback } from "react";
import { getReferentielDataFromServer } from "../components/general/AxiosReferencielInterceptor";

export const useProfileImage = (options = {}) => {
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [loadingImage, setLoadingImage] = useState(false);
  const [error, setError] = useState(null);

  const retryTimeouts = useRef([]);
  const retryCount = useRef(0);
  const maxRetries = options.maxRetries || 5;

  const cleanupUrl = useCallback(() => {
    if (profileImageUrl) {
      URL.revokeObjectURL(profileImageUrl);
      setProfileImageUrl(null);
    }
  }, [profileImageUrl]);

  const clearRetryTimeouts = useCallback(() => {
    retryTimeouts.current.forEach((timeout) => clearTimeout(timeout));
    retryTimeouts.current = [];
  }, []);

  const fetchProfileImage = useCallback(
    async (imageGuid, imageFileName, isRetry = false) => {
      if (!imageGuid) {
        setLoadingImage(false);
        return;
      }
  
      try {
        if (!isRetry) {
          setLoadingImage(true);
          retryCount.current = 0;
          setError(null);
        }
  
        const timestamp = new Date().getTime();
        const response = await getReferentielDataFromServer(
          false,
          `/api/document/${imageGuid}?t=${timestamp}`,
          {},
          "post",
          { responseType: "arraybuffer" }
        );
  
        if (!response || response.byteLength === 0) {
          throw new Error("Empty image response received");
        }
  
        let contentType = "image/jpeg";
        if (imageFileName) {
          const fileName = imageFileName.toLowerCase();
          if (fileName.endsWith(".png")) {
            contentType = "image/png";
          } else if (fileName.endsWith(".gif")) {
            contentType = "image/gif";
          }
        }
  
        cleanupUrl();
  
        const blob = new Blob([response], { type: contentType });
        const url = URL.createObjectURL(blob);
  
        return new Promise((resolve, reject) => {
          const img = new Image();
          let imageLoadTimeout;
  
          imageLoadTimeout = setTimeout(() => {
            img.onerror = null; 
            handleImageError();
          }, 5000);
  
          const handleImageError = () => {
            clearTimeout(imageLoadTimeout);
            URL.revokeObjectURL(url);
            
            if (retryCount.current < maxRetries) {
              scheduleRetry();
            } else {
              setLoadingImage(false);
              setError("Failed to load image after multiple attempts");
              reject(new Error("Maximum retries reached"));
            }
          };
  
          const scheduleRetry = () => {
            retryCount.current++;
            
            const baseDelay = Math.min(1000 * Math.pow(2, retryCount.current), 30000);
            const jitter = Math.random() * 1000; // NOSONAR - Safe for non-crypto use (retry delay)
            const delay = Math.min(baseDelay + jitter, 30000); 
            
            console.log(
              `Retrying in ${Math.round(delay)}ms... (Attempt ${retryCount.current}/${maxRetries})`
            );
  
            const timeoutId = setTimeout(() => {
              fetchProfileImage(imageGuid, imageFileName, true)
                .then(resolve)
                .catch(reject);
            }, delay);
  
            retryTimeouts.current.push(timeoutId);
          };
  
          img.onload = () => {
            clearTimeout(imageLoadTimeout);
            setProfileImageUrl(url);
            setLoadingImage(false);
            clearRetryTimeouts();
            resolve(url);
          };
  
          img.onerror = handleImageError;
  
          img.src = url;
        });
      } catch (error) {
        console.error("Error fetching profile image:", error);
  
        if (retryCount.current < maxRetries) {
          retryCount.current++;
          
          const baseDelay = Math.min(1000 * Math.pow(2, retryCount.current), 30000);
          const jitter = Math.random() * 1000; // NOSONAR - Safe for non-crypto use (retry delay)
          const delay = Math.min(baseDelay + jitter, 30000);
          
          console.log(
            `Retrying after error in ${Math.round(delay)}ms... (Attempt ${retryCount.current}/${maxRetries})`
          );
  
          const timeoutId = setTimeout(() => {
            fetchProfileImage(imageGuid, imageFileName, true).catch(console.error);
          }, delay);
  
          retryTimeouts.current.push(timeoutId);
        } else {
          setLoadingImage(false);
          setError("Failed to load profile image after multiple attempts");
        }
      }
    },
    [cleanupUrl, clearRetryTimeouts, maxRetries]
  );

  useEffect(() => {
    return () => {
      clearRetryTimeouts();
      cleanupUrl();
    };
  }, [clearRetryTimeouts, cleanupUrl]);

  return {
    profileImageUrl,
    loadingImage,
    error,
    fetchProfileImage,
    clearProfileImage: cleanupUrl,
  };
};
